import {
  IconSecuritySecure,
  PlaybackKey,
  PlaybackValue,
} from '@piggybank/core';
import first from 'lodash/first';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import T from 'prop-types';
import React from 'react';
import { DynamicFormReadOnlyFieldFormatter } from './DynamicFormReadOnlyFieldFormatter';
import {
  Optional,
  Translation,
  TranslationScope,
  Translations,
  useCultureConfiguration,
} from './context';
import styles from './style.css';
import { formNameFromKeyPath, localizationKeyPath, reducei18n } from './utils';

const DynamicFormReadOnlyField = ({
  formValues,
  formData,
  metaData: {
    componentName = '',
    componentProps,
    context,
    isCheckboxGroup = false,
    translations: { label: { fallbackPath, path = 'label' } = {} } = {},
  },
  readOnly = false,
  reviewPage = false,
  validate,
  formatter = null,
}) => {
  const culture = useCultureConfiguration();

  if (typeof formData === 'undefined') return null;

  const reviewFormatter =
    formatter ||
    DynamicFormReadOnlyFieldFormatter[
      isCheckboxGroup ? 'CheckboxGroup' : componentName
    ] ||
    DynamicFormReadOnlyFieldFormatter['fallback'];

  return (
    <TranslationScope path={localizationKeyPath(context.keyPath).join('.')}>
      <PlaybackKey marginBottom={readOnly && !reviewPage ? 2 : 0}>
        {readOnly && (
          <span className={styles.icon}>
            <IconSecuritySecure inline />
          </span>
        )}
        <Optional>
          <Translations>
            {({ translations }) => {
              const keyLabel = `${localizationKeyPath(context.keyPath).join(
                '.'
              )}`;

              const absolutePath = `reviewDetails.${keyLabel}.${path}`;

              const fallbackAbsolutePath = fallbackPath
                ? `reviewDetails.${keyLabel}.${fallbackPath}`
                : undefined;

              return has(translations, absolutePath) ||
                (fallbackAbsolutePath &&
                  has(translations, fallbackAbsolutePath)) ? (
                <Translation
                  absolutePath={absolutePath}
                  fallbackAbsolutePath={fallbackAbsolutePath}
                />
              ) : (
                <Translation fallbackPath={fallbackPath} path={path}>
                  {translation => {
                    if (isArray(translation)) {
                      return first(
                        reducei18n(
                          translation,
                          formValues,
                          formNameFromKeyPath(context.keyPath)
                        )
                      ).source;
                    }

                    return translation;
                  }}
                </Translation>
              );
            }}
          </Translations>
        </Optional>
      </PlaybackKey>
      <PlaybackValue>
        <Translations>
          {({ translations }) =>
            reviewFormatter({
              context,
              culture,
              componentProps,
              formData,
              translations,
              validate,
            })
          }
        </Translations>
      </PlaybackValue>
    </TranslationScope>
  );
};

DynamicFormReadOnlyField.propTypes = {
  formValues: T.object,
  formData: T.oneOfType([T.string, T.number, T.bool, T.object, T.array]),
  metaData: T.shape({
    componentName: T.string,
    componentProps: T.object,
    context: T.shape({
      keyPath: T.object,
    }),
    isCheckboxGroup: T.bool,
    translations: T.shape({
      paths: T.shape({
        label: T.string,
      }),
    }),
  }),
  readOnly: T.bool,
  reviewPage: T.bool,
  validate: T.oneOfType([T.object, T.func]),
  formatter: T.func,
};

export default DynamicFormReadOnlyField;
